import { DateTimeEditor, DropdownEditor, Field, TextEditor } from '@approvalmax/ui';
import { Grid } from '@approvalmax/ui/src/components';
import { useAtomValue } from 'jotai';
import { domain } from 'modules/data';
import { PlainDataProvider } from 'modules/data-providers';
import { useSelector } from 'modules/react-redux';
import { SectionContainer } from 'pages/requestForm/components';
import { getNetSuiteContext } from 'pages/requestForm/selectors/netsuite/netSuiteSelectors';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { usePrevious } from 'react-use';
import { useGetNetSuiteCustomer } from 'shared/data/webApp/v2';
import { useNetSuitePurchaseOrderFieldSettings } from 'shared/hooks';

import { NetSuiteCustomersDataProvider, NetSuiteShippingMethodsDataProvider } from '../../../components';
import { NetSuiteExtendedCustomer } from '../../../data/types';
import { convertIntegrationCodeToDocumentType } from '../../../helpers';
import {
    isFieldEditable,
    isFieldMandatory,
    isFieldReadOnly,
    isFieldVisible,
    isOptionalFieldInvalid,
} from '../../../NetSuiteRequestForm/NetSuiteRequestForm.helpers';
import { netSuiteShowErrorsState } from '../../../resources/states';
import { useNetSuitePurchaseOrderFields } from '../../NetSuitePurchaseOrderRequestForm.hooks';
import { netSuitePurchaseOrderFieldValuesState } from '../../NetSuitePurchaseOrderRequestForm.states';
import { PurchaseOrderFields } from '../../NetSuitePurchaseOrderRequestForm.types';
import { mapShipToData } from './ShippingDetailsSection.helpers';
import { messages } from './ShippingDetailsSection.messages';
import { ShippingDetailsSectionProps } from './ShippingDetailsSection.types';

export const ShippingDetailsSection = memo((props: ShippingDetailsSectionProps) => {
    const { companyId } = props;

    const netSuiteEditingContext = useSelector(getNetSuiteContext);
    const showErrors = useAtomValue(netSuiteShowErrorsState);
    const fieldValues = useAtomValue(netSuitePurchaseOrderFieldValuesState);

    const { documentFields } = netSuiteEditingContext;

    const { onFieldChange, updateFieldsBulk } = useNetSuitePurchaseOrderFields();

    const fieldSettings = useNetSuitePurchaseOrderFieldSettings(documentFields);

    const { data: netSuiteCustomer, isFetching: isFetchingGetNetSuiteCustomer } = useGetNetSuiteCustomer({
        path: {
            companyId: companyId,
            customerId: fieldValues.shipTo?.id,
            documentType: convertIntegrationCodeToDocumentType(domain.IntegrationCode.NetSuitePO),
        },
    });

    const shipToData = useMemo(() => mapShipToData(netSuiteCustomer), [netSuiteCustomer]);

    const previousShipToData = usePrevious(shipToData);

    const [shouldUpdateShipToData, setShouldUpdateShipToData] = useState(false);

    useEffect(() => {
        if (shouldUpdateShipToData && shipToData && previousShipToData?.id !== shipToData?.id) {
            setShouldUpdateShipToData(false);

            onFieldChange(
                'shippingAddress',
                shipToData.addresses.find(({ isDefaultShipping }) => isDefaultShipping) ?? null
            );
        }
    }, [shouldUpdateShipToData, previousShipToData, shipToData, onFieldChange]);

    const isShippingDetailsSectionVisible = useMemo(
        () =>
            isFieldVisible(fieldSettings.shipTo) ||
            isFieldVisible(fieldSettings.shippingAddress) ||
            isFieldVisible(fieldSettings.shipDate) ||
            isFieldVisible(fieldSettings.shipMethod) ||
            isFieldVisible(fieldSettings.fob) ||
            isFieldVisible(fieldSettings.trackingNumbers),
        [fieldSettings]
    );

    const onEditorChange = useCallback(
        <K extends keyof PurchaseOrderFields>(key: K) =>
            (value: PurchaseOrderFields[K]) =>
                onFieldChange(key, value),
        [onFieldChange]
    );

    const onShipToChange = useCallback(
        (shipTo: NetSuiteExtendedCustomer | null) => {
            updateFieldsBulk({
                shipTo,
                shippingAddress: null,
            });

            setShouldUpdateShipToData(true);
        },
        [updateFieldsBulk]
    );

    if (!isShippingDetailsSectionVisible) {
        return null;
    }

    return (
        <SectionContainer title={messages.shippingDetailsSectionTitle}>
            <Grid gridTemplateColumns='repeat(auto-fill, 267px)' rowGap={24} columnGap={32}>
                {isFieldVisible(fieldSettings.shipTo) && (
                    <Field title={messages.shipToFieldText} required={isFieldMandatory(fieldSettings.shipTo)}>
                        <NetSuiteCustomersDataProvider
                            integrationCode={domain.IntegrationCode.NetSuitePO}
                            subsidiaryId={fieldValues.subsidiary?.id}
                        >
                            <DropdownEditor
                                value={fieldValues.shipTo}
                                singleLinePerItem={false}
                                onChange={onShipToChange}
                                placeholder={messages.shipToFieldPlaceholder}
                                invalid={isOptionalFieldInvalid(showErrors, fieldValues.shipTo, fieldSettings.shipTo)}
                            />
                        </NetSuiteCustomersDataProvider>
                    </Field>
                )}

                {isFieldVisible(fieldSettings.shippingAddress) && (
                    <Field
                        title={messages.shippingAddressFieldText}
                        required={isFieldMandatory(fieldSettings.shippingAddress)}
                    >
                        <PlainDataProvider items={shipToData?.addresses ?? []}>
                            <DropdownEditor
                                value={fieldValues.shippingAddress || null}
                                singleLinePerItem={false}
                                onChange={onEditorChange('shippingAddress')}
                                placeholder={
                                    isFieldEditable(fieldSettings.shippingAddress)
                                        ? messages.shippingAddressFieldPlaceholder
                                        : ''
                                }
                                invalid={isOptionalFieldInvalid(
                                    showErrors,
                                    fieldValues.shippingAddress,
                                    fieldSettings.shippingAddress
                                )}
                                displayAttribute='fullAddress'
                                loading={isFetchingGetNetSuiteCustomer}
                                disabled={isFieldReadOnly(fieldSettings.shippingAddress)}
                            />
                        </PlainDataProvider>
                    </Field>
                )}

                {isFieldVisible(fieldSettings.shipDate) && (
                    <Field title={messages.shipDateFieldText} required={isFieldMandatory(fieldSettings.shipDate)}>
                        <DateTimeEditor
                            value={fieldValues.shipDate}
                            placeholder={isFieldEditable(fieldSettings.shipDate) ? messages.dateFieldPlaceholder : ''}
                            disabled={isFieldReadOnly(fieldSettings.shipDate)}
                            invalid={isOptionalFieldInvalid(showErrors, fieldValues.shipDate, fieldSettings.shipDate)}
                            onChange={onEditorChange('shipDate')}
                        />
                    </Field>
                )}

                {isFieldVisible(fieldSettings.shipMethod) && (
                    <Field title={messages.shipMethodFieldText} required={isFieldMandatory(fieldSettings.shipMethod)}>
                        <NetSuiteShippingMethodsDataProvider
                            integrationCode={domain.IntegrationCode.NetSuitePO}
                            customerId={fieldValues.shipTo?.id}
                        >
                            <DropdownEditor
                                value={fieldValues.shipMethod || null}
                                singleLinePerItem={false}
                                onChange={onEditorChange('shipMethod')}
                                placeholder={
                                    isFieldEditable(fieldSettings.shipMethod) ? messages.shipMethodFieldPlaceholder : ''
                                }
                                invalid={isOptionalFieldInvalid(
                                    showErrors,
                                    fieldValues.shipMethod,
                                    fieldSettings.shipMethod
                                )}
                                disabled={isFieldReadOnly(fieldSettings.shipMethod)}
                            />
                        </NetSuiteShippingMethodsDataProvider>
                    </Field>
                )}

                {isFieldVisible(fieldSettings.fob) && (
                    <Field title={messages.fobFieldText} required={isFieldMandatory(fieldSettings.fob)}>
                        <TextEditor
                            disabled={isFieldReadOnly(fieldSettings.fob)}
                            value={fieldValues.freeOnBoard ?? ''}
                            onChange={onEditorChange('freeOnBoard')}
                            placeholder={messages.textFieldPlaceholder}
                            invalid={isOptionalFieldInvalid(showErrors, fieldValues.freeOnBoard, fieldSettings.fob)}
                        />
                    </Field>
                )}

                {isFieldVisible(fieldSettings.trackingNumbers) && (
                    <Field
                        title={messages.trackingNumbersFieldText}
                        required={isFieldMandatory(fieldSettings.trackingNumbers)}
                    >
                        <TextEditor
                            disabled={isFieldReadOnly(fieldSettings.trackingNumbers)}
                            value={fieldValues.trackingNumbers ?? ''}
                            onChange={onEditorChange('trackingNumbers')}
                            placeholder={messages.textFieldPlaceholder}
                            invalid={isOptionalFieldInvalid(
                                showErrors,
                                fieldValues.trackingNumbers,
                                fieldSettings.trackingNumbers
                            )}
                        />
                    </Field>
                )}
            </Grid>
        </SectionContainer>
    );
});

ShippingDetailsSection.displayName = 'ShippingDetailsSection';
